import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, setDoc, updateDoc, increment } from 'firebase/firestore';

export const getImageProcessingMetrics = async (userId) => {
  try {
    // Get the user's metrics document
    const userMetricsDoc = await getDoc(doc(db, 'userMetrics', userId));
    
    if (!userMetricsDoc.exists()) {
      console.log('No metrics found for user');
      return {
        totalStats: {
          imagesProcessed: 0,
          storageSaved: 0,
          compressionRatio: 100
        },
        dailyData: [],
        monthlyData: []
      };
    }

    const metrics = userMetricsDoc.data();
    
    // Get the values from the actual structure
    const totalImagesProcessed = parseInt(metrics.totalImagesProcessed || 0);
    const totalBytesOriginalMB = parseFloat(metrics.totalBytesOriginalMB || 0);
    const totalBytesProcessedMB = parseFloat(metrics.totalBytesProcessedMB || 0);
    const totalBytesSavedMB = parseFloat(metrics.totalBytesSavedMB || 0);
    const averageCompressionRatio = parseFloat(metrics.averageCompressionRatio || 100);
    const lastUpdated = metrics.lastUpdated;

    // Get historical data from the imageProcessingHistory collection
    const historyRef = collection(db, 'userMetrics', userId, 'imageProcessingHistory');
    const historyQuery = query(historyRef, orderBy('date', 'asc'));
    const historySnapshot = await getDocs(historyQuery);
    
    // Process daily data
    const dailyData = [];
    const monthlyDataMap = new Map();
    
    historySnapshot.forEach(doc => {
      const data = doc.data();
      const date = data.date.split('T')[0];
      const month = date.substring(0, 7); // YYYY-MM format
      
      // Add to daily data
      dailyData.push({
        date,
        imagesProcessed: data.imagesProcessed || 0,
        storageSaved: data.bytesSavedMB || 0
      });
      
      // Aggregate monthly data
      if (!monthlyDataMap.has(month)) {
        monthlyDataMap.set(month, {
          month,
          imagesProcessed: 0,
          storageSaved: 0
        });
      }
      
      const monthData = monthlyDataMap.get(month);
      monthData.imagesProcessed += data.imagesProcessed || 0;
      monthData.storageSaved += data.bytesSavedMB || 0;
    });
    
    // If no historical data exists but we have total stats, create at least one data point
    if (dailyData.length === 0 && totalImagesProcessed > 0) {
      const today = lastUpdated ? lastUpdated.split('T')[0] : new Date().toISOString().split('T')[0];
      const month = today.substring(0, 7);
      
      dailyData.push({
        date: today,
        imagesProcessed: totalImagesProcessed,
        storageSaved: Math.round(totalBytesSavedMB)
      });
      
      monthlyDataMap.set(month, {
        month,
        imagesProcessed: totalImagesProcessed,
        storageSaved: Math.round(totalBytesSavedMB)
      });
    }
    
    // Convert monthly map to array and sort by month
    const monthlyData = Array.from(monthlyDataMap.values())
      .sort((a, b) => a.month.localeCompare(b.month));
    
    // Ensure we have data for the last 6 months (fill with zeros for missing months)
    const last6Months = Array.from({length: 6}, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (5 - i));
      return date.toISOString().slice(0, 7); // YYYY-MM format
    });
    
    const completeMonthlyData = last6Months.map(month => {
      const existingData = monthlyData.find(data => data.month === month);
      return existingData || {
        month,
        imagesProcessed: 0,
        storageSaved: 0
      };
    });

    return {
      totalStats: {
        imagesProcessed: totalImagesProcessed,
        storageSaved: totalBytesSavedMB * 1024 * 1024, // Convert MB to bytes
        compressionRatio: Number(averageCompressionRatio) // Ensure it's a number
      },
      dailyData: dailyData.sort((a, b) => a.date.localeCompare(b.date)),
      monthlyData: completeMonthlyData
    };
  } catch (error) {
    console.error('Error fetching image processing metrics:', error);
    throw error;
  }
};

/**
 * Records image processing metrics for a specific date
 * @param {string} userId - The user ID
 * @param {number} imagesProcessed - Number of images processed
 * @param {number} bytesOriginalMB - Original size in MB
 * @param {number} bytesProcessedMB - Processed size in MB
 * @param {number} bytesSavedMB - Bytes saved in MB
 * @param {number} compressionRatio - Compression ratio percentage
 * @param {string} date - ISO date string (YYYY-MM-DD)
 */
export const recordImageProcessingHistory = async (
  userId, 
  imagesProcessed, 
  bytesOriginalMB, 
  bytesProcessedMB, 
  bytesSavedMB, 
  compressionRatio,
  date = new Date().toISOString().split('T')[0]
) => {
  try {
    // Reference to the user metrics document
    const userMetricsRef = doc(db, 'userMetrics', userId);
    
    // Get existing user metrics
    const userMetricsDoc = await getDoc(userMetricsRef);
    const userMetrics = userMetricsDoc.exists() ? userMetricsDoc.data() : {};
    
    // Update total metrics
    const totalImagesProcessed = (userMetrics.totalImagesProcessed || 0) + imagesProcessed;
    const totalBytesOriginalMB = (userMetrics.totalBytesOriginalMB || 0) + bytesOriginalMB;
    const totalBytesProcessedMB = (userMetrics.totalBytesProcessedMB || 0) + bytesProcessedMB;
    const totalBytesSavedMB = (userMetrics.totalBytesSavedMB || 0) + bytesSavedMB;
    
    // Calculate new average compression ratio
    const newCompressionRatio = totalBytesOriginalMB > 0 
      ? ((totalBytesOriginalMB - totalBytesProcessedMB) / totalBytesOriginalMB) * 100 
      : 0;
    
    // Update or create the user metrics document
    if (userMetricsDoc.exists()) {
      // Update existing document
      await updateDoc(userMetricsRef, {
        totalImagesProcessed,
        totalBytesOriginalMB,
        totalBytesProcessedMB,
        totalBytesSavedMB,
        averageCompressionRatio: newCompressionRatio,
        lastUpdated: new Date().toISOString()
      });
    } else {
      // Create new document
      await setDoc(userMetricsRef, {
        totalImagesProcessed,
        totalBytesOriginalMB,
        totalBytesProcessedMB,
        totalBytesSavedMB,
        averageCompressionRatio: newCompressionRatio,
        lastUpdated: new Date().toISOString()
      });
    }
    
    // Reference to the daily history document
    const historyDocRef = doc(db, 'userMetrics', userId, 'imageProcessingHistory', date);
    
    // Get existing history for this date if it exists
    const historyDoc = await getDoc(historyDocRef);
    
    if (historyDoc.exists()) {
      // Update existing history
      const existingData = historyDoc.data();
      await updateDoc(historyDocRef, {
        imagesProcessed: (existingData.imagesProcessed || 0) + imagesProcessed,
        bytesOriginalMB: (existingData.bytesOriginalMB || 0) + bytesOriginalMB,
        bytesProcessedMB: (existingData.bytesProcessedMB || 0) + bytesProcessedMB,
        bytesSavedMB: (existingData.bytesSavedMB || 0) + bytesSavedMB,
        lastUpdated: new Date().toISOString()
      });
    } else {
      // Create new history document
      await setDoc(historyDocRef, {
        date: new Date().toISOString(),
        imagesProcessed,
        bytesOriginalMB,
        bytesProcessedMB,
        bytesSavedMB,
        compressionRatio,
        lastUpdated: new Date().toISOString()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error recording image processing history:', error);
    throw error;
  }
};
